import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

import {
    Badge,
    Container,
    Group,
    Paper,
    Stack,
    Tabs,
    Text,
    Title,
} from "@mantine/core";

import { notifications } from "@mantine/notifications";

import {
    IconBed,
    IconPlus,
} from "@tabler/icons-react";

import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/Billede1.jpg";

import {
    getEvents,
    type EventItem,
} from "../../api/shiftsApi";

import {
    createHotelBooking,
    getHotelBookingsForUserEvent,
    getHotelsForEvent,
    HotelBookingDto,
    HotelBookingUpdateDto,
    HotelDto,
    updateHotelBooking,
} from "../../api/hotelBookingApi";

import { useLanguage } from "../../context/LanguageContext";

import BookingDetailsModal from "../../components/hotel/BookingDetailsModal";
import BookingList from "../../components/hotel/BookingList";
import CreateBookingForm from "../../components/hotel/CreateBookingForm";
import EditBookingModal from "../../components/hotel/EditBookingModal";

import {
    CreateHotelBookingFormPayload,
    HotelBookingTexts,
} from "../../components/hotel/HotelBookingTypes";

import {
    normalizeStatus,
} from "../../utils/hotelBookingUtils";


export default function HotelBookingPage() {
    const apiUrl =
        process.env.REACT_APP_API_URL as string;

    const {
        language,
        setLanguage,
    } = useLanguage();

    const {
        userId,
        eventId,
    } = useParams();

    const navigate = useNavigate();

    const parsedUserId =
        Number(userId);

    const parsedEventId =
        Number(eventId);


    // ------------------------------------------------------
    // Data
    // ------------------------------------------------------

    const [
        events,
        setEvents,
    ] = useState<EventItem[]>([]);

    const [
        hotels,
        setHotels,
    ] = useState<HotelDto[]>([]);

    const [
        bookings,
        setBookings,
    ] = useState<HotelBookingDto[]>([]);


    // ------------------------------------------------------
    // Tabs
    // ------------------------------------------------------

    const [
        activeTab,
        setActiveTab,
    ] = useState<string | null>("bookings");


    // ------------------------------------------------------
    // View booking
    // ------------------------------------------------------

    const [
        viewBookingOpen,
        setViewBookingOpen,
    ] = useState(false);

    const [
        selectedBooking,
        setSelectedBooking,
    ] =
        useState<HotelBookingDto | null>(
            null
        );


    // ------------------------------------------------------
    // Edit booking
    // ------------------------------------------------------

    const [
        editBookingOpen,
        setEditBookingOpen,
    ] = useState(false);

    const [
        editingBooking,
        setEditingBooking,
    ] =
        useState<HotelBookingDto | null>(
            null
        );

    const [
        savingEdit,
        setSavingEdit,
    ] = useState(false);


    // ------------------------------------------------------
    // Filters
    // ------------------------------------------------------

    const [
        bookingSearch,
        setBookingSearch,
    ] = useState("");

    const [
        bookingStatusFilter,
        setBookingStatusFilter,
    ] =
        useState<string | null>(
            "all"
        );


    // ------------------------------------------------------
    // Loading
    // ------------------------------------------------------

    const [
        saving,
        setSaving,
    ] = useState(false);

    const [
        loadingBookings,
        setLoadingBookings,
    ] = useState(false);


    // ------------------------------------------------------
    // Language
    // ------------------------------------------------------

    useEffect(() => {
        setLanguage("en");
    }, [setLanguage]);


    // ------------------------------------------------------
    // Text
    // ------------------------------------------------------

    const texts: Record<
        "en" | "da",
        HotelBookingTexts
    > = {
        en: {
            heroTitle: "Hotel Booking",

            heroDesc:
                "Book hotel rooms for players, coaches and staff.",

            event: "Event",

            myBookings:
                "My bookings",

            createBooking:
                "Create booking",

            myBookingsText:
                "Follow your previous bookings, payment status and rooming list.",

            noBookings:
                "You have no hotel bookings for this event yet.",

            booking: "Booking",
            hotel: "Hotel",
            total: "Total",
            rooms: "Rooms",
            created: "Created",
            view: "View",

            payNow:
                "Pay now",

            fillGuests:
                "Fill guests",

            continueRooming:
                "Continue rooming list",

            stepTitle:
                "Step 1 – Book rooms",

            stepSubtitle:
                "Book one or more rooms with individual dates.",

            chooseHotel:
                "Choose hotel",

            chooseHotelText:
                "Select the hotel this booking should be created for.",

            noHotels:
                "No active hotels are available for this event.",

            selected:
                "Selected",

            fromPrice:
                "From",

            roomTypes:
                "Room types",

            deadline:
                "Deadline",

            noAddress:
                "No address",

            contactInfo:
                "Contact information",

            country:
                "Country",

            contactPerson:
                "Contact person",

            contactEmail:
                "Contact email",

            addRoom:
                "Add room",

            roomType:
                "Room type",

            chooseType:
                "Choose type",

            checkIn:
                "Check-in",

            checkOut:
                "Check-out",

            guests:
                "Guests",

            addToList:
                "Add to list",

            roomBookings:
                "Room bookings",

            priceNight:
                "Price / night",

            nights:
                "Nights",

            actions:
                "Actions",

            summary:
                "Summary",

            totalPrice:
                "Total price",

            comments:
                "Comments",

            commentsPlaceholder:
                "Add any special requests or comments...",

            submitInfo:
                "After you submit, your booking will be sent for approval.",

            submit:
                "Submit booking for approval",

            missing:
                "Missing information",

            error:
                "Error",

            bookingSent:
                "Booking submitted",

            bookingSentMessage:
                "has been submitted for approval",

            cannotSubmit:
                "Could not submit booking",

            noRooms:
                "No rooms added yet.",

            roomingProgress:
                "Rooming list",

            filled:
                "filled",

            missingNames:
                "missing",

            roomingDeadline:
                "Rooming deadline",

            deadlinePassed:
                "Deadline has passed. You can view the rooming list, but no longer edit it.",

            back:
                "Back",
        },

        da: {
            heroTitle:
                "Hotelbooking",

            heroDesc:
                "Book hotelværelser til spillere, coaches og staff.",

            event:
                "Event",

            myBookings:
                "Mine bookinger",

            createBooking:
                "Opret booking",

            myBookingsText:
                "Følg dine tidligere bookinger, betalingsstatus og rooming list.",

            noBookings:
                "Du har ingen hotelbookinger til dette event endnu.",

            booking:
                "Booking",

            hotel:
                "Hotel",

            total:
                "Total",

            rooms:
                "Værelser",

            created:
                "Oprettet",

            view:
                "Vis",

            payNow:
                "Betal nu",

            fillGuests:
                "Udfyld gæster",

            continueRooming:
                "Fortsæt rooming list",

            stepTitle:
                "Step 1 – Book værelser",

            stepSubtitle:
                "Book et eller flere værelser med individuelle datoer.",

            chooseHotel:
                "Vælg hotel",

            chooseHotelText:
                "Vælg det hotel bookingen skal oprettes på.",

            noHotels:
                "Der er ikke oprettet aktive hoteller til dette event.",

            selected:
                "Valgt",

            fromPrice:
                "Fra",

            roomTypes:
                "Værelsestyper",

            deadline:
                "Deadline",

            noAddress:
                "Ingen adresse",

            contactInfo:
                "Kontaktoplysninger",

            country:
                "Land",

            contactPerson:
                "Kontaktperson",

            contactEmail:
                "Kontakt email",

            addRoom:
                "Tilføj værelse",

            roomType:
                "Værelsestype",

            chooseType:
                "Vælg type",

            checkIn:
                "Check-in",

            checkOut:
                "Check-out",

            guests:
                "Gæster",

            addToList:
                "Tilføj til liste",

            roomBookings:
                "Værelsesbookinger",

            priceNight:
                "Pris / nat",

            nights:
                "Nætter",

            actions:
                "Handlinger",

            summary:
                "Opsummering",

            totalPrice:
                "Samlet pris",

            comments:
                "Kommentarer",

            commentsPlaceholder:
                "Tilføj særlige ønsker eller kommentarer...",

            submitInfo:
                "Efter du sender bookingen, sendes den til godkendelse.",

            submit:
                "Send booking til godkendelse",

            missing:
                "Mangler",

            error:
                "Fejl",

            bookingSent:
                "Booking sendt",

            bookingSentMessage:
                "er sendt til godkendelse",

            cannotSubmit:
                "Kunne ikke sende booking",

            noRooms:
                "Ingen værelser tilføjet endnu.",

            roomingProgress:
                "Rooming list",

            filled:
                "udfyldt",

            missingNames:
                "mangler",

            roomingDeadline:
                "Rooming deadline",

            deadlinePassed:
                "Deadline er overskredet. Du kan se rooming list, men ikke længere rette den.",

            back:
                "Tilbage",
        },
    };

    const t =
        texts[language];


    // ------------------------------------------------------
    // Filter bookings
    // ------------------------------------------------------

    const filteredBookings =
        useMemo(() => {
            const search =
                bookingSearch
                    .trim()
                    .toLowerCase();

            return bookings.filter(
                (booking) => {
                    const status =
                        normalizeStatus(
                            booking.status
                        );

                    const matchesStatus =
                        bookingStatusFilter ===
                            "all" ||
                            !bookingStatusFilter
                            ? true
                            : status ===
                            bookingStatusFilter;

                    const matchesSearch =
                        !search ||
                        String(
                            booking.hotelBookingID
                        ).includes(search) ||
                        booking.hotelName
                            ?.toLowerCase()
                            .includes(search) ||
                        booking.country
                            ?.toLowerCase()
                            .includes(search) ||
                        booking.contactName
                            ?.toLowerCase()
                            .includes(search) ||
                        booking.contactEmail
                            ?.toLowerCase()
                            .includes(search);

                    return (
                        matchesStatus &&
                        matchesSearch
                    );
                }
            );
        }, [
            bookings,
            bookingSearch,
            bookingStatusFilter,
        ]);


    // ------------------------------------------------------
    // Selected event
    // ------------------------------------------------------

    const selectedEvent =
        useMemo(
            () =>
                events.find(
                    (e) =>
                        e.eventID ===
                        parsedEventId
                ),
            [
                events,
                parsedEventId,
            ]
        );


    // ------------------------------------------------------
    // Load events
    // ------------------------------------------------------

    useEffect(() => {
        async function loadEvents() {
            try {
                setEvents(
                    await getEvents(
                        apiUrl
                    )
                );
            } catch {
                notifications.show({
                    color: "red",
                    title: t.error,
                    message:
                        "Could not load events",
                });
            }
        }

        loadEvents();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl]);


    // ------------------------------------------------------
    // Auto select tab
    // ------------------------------------------------------

    useEffect(() => {
        if (loadingBookings) {
            return;
        }

        if (
            bookings.length === 0
        ) {
            setActiveTab(
                "create"
            );
        } else {
            setActiveTab(
                "bookings"
            );
        }
    }, [
        loadingBookings,
        bookings.length,
    ]);


    // ------------------------------------------------------
    // Load hotels
    // ------------------------------------------------------

    useEffect(() => {
        async function loadHotels() {
            if (
                !parsedEventId ||
                parsedEventId <= 0
            ) {
                setHotels([]);
                return;
            }

            try {
                setHotels(
                    await getHotelsForEvent(
                        apiUrl,
                        parsedEventId
                    )
                );
            } catch {
                notifications.show({
                    color: "red",
                    title: t.error,
                    message:
                        "Could not load hotels",
                });
            }
        }

        loadHotels();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        apiUrl,
        parsedEventId,
    ]);


    // ------------------------------------------------------
    // Load user bookings
    // ------------------------------------------------------

    async function loadUserBookings() {
        if (
            !parsedUserId ||
            !parsedEventId
        ) {
            return;
        }

        setLoadingBookings(true);

        try {
            const data =
                await getHotelBookingsForUserEvent(
                    apiUrl,
                    parsedUserId,
                    parsedEventId
                );

            setBookings(
                Array.isArray(data)
                    ? data
                    : []
            );
        } catch {
            setBookings([]);
        } finally {
            setLoadingBookings(false);
        }
    }

    useEffect(() => {
        loadUserBookings();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        apiUrl,
        parsedUserId,
        parsedEventId,
    ]);


    // ------------------------------------------------------
    // View booking
    // ------------------------------------------------------

    function openViewBooking(
        booking: HotelBookingDto
    ) {
        setSelectedBooking(
            booking
        );

        setViewBookingOpen(
            true
        );
    }


    // ------------------------------------------------------
    // Edit booking
    // ------------------------------------------------------

    function openEditBooking(booking: HotelBookingDto) {
        const status = normalizeStatus(booking.status);

        const canEdit =
            !booking.isPaid &&
            !booking.paidAt &&
            (
                status === "pendingapproval" ||
                status === "awaitingpayment"
            );

        if (!canEdit) {
            notifications.show({
                color: "red",
                title:
                    language === "en"
                        ? "Booking locked"
                        : "Booking låst",
                message:
                    language === "en"
                        ? "This booking can no longer be edited."
                        : "Denne booking kan ikke længere rettes.",
            });

            return;
        }

        setEditingBooking(booking);
        setEditBookingOpen(true);
    }


    // ------------------------------------------------------
    // Save booking changes
    // ------------------------------------------------------

    async function saveBookingChanges(
        booking: HotelBookingDto,
        payload: HotelBookingUpdateDto
    ): Promise<boolean> {
        setSavingEdit(true);

        try {
            await updateHotelBooking(
                apiUrl,
                booking.hotelBookingID,
                payload
            );

            const wasAwaitingPayment =
                normalizeStatus(booking.status) === "awaitingpayment";

            notifications.show({
                color: "green",
                title:
                    language === "en"
                        ? "Booking updated"
                        : "Booking opdateret",
                message: wasAwaitingPayment
                    ? language === "en"
                        ? "Your booking has been updated and sent for approval again."
                        : "Din booking er blevet rettet og sendt til godkendelse igen."
                    : language === "en"
                        ? "Your booking has been updated."
                        : "Din booking er blevet opdateret.",
            });

            await loadUserBookings();

            setEditingBooking(null);
            setEditBookingOpen(false);

            return true;
        } catch (e: any) {
            notifications.show({
                color: "red",
                title:
                    language === "en"
                        ? "Could not update booking"
                        : "Kunne ikke rette booking",
                message:
                    e?.message ||
                    (language === "en"
                        ? "An error occurred while updating the booking."
                        : "Der opstod en fejl under opdateringen."),
            });

            return false;
        } finally {
            setSavingEdit(false);
        }
    }


    // ------------------------------------------------------
    // Payment
    // ------------------------------------------------------

    function handlePayNow(
        booking: HotelBookingDto
    ) {
        if (
            !booking.paymentLink
        ) {
            notifications.show({
                color: "red",

                title:
                    language === "en"
                        ? "Payment unavailable"
                        : "Betaling ikke mulig",

                message:
                    language === "en"
                        ? "This booking cannot be paid yet."
                        : "Denne booking kan ikke betales endnu.",
            });

            return;
        }

        window.location.href =
            booking.paymentLink;
    }


    // ------------------------------------------------------
    // Rooming
    // ------------------------------------------------------

    function handleRoomingList(
        booking: HotelBookingDto
    ) {
        navigate(
            `/hotel/booking/${parsedUserId}/${parsedEventId}/${booking.hotelBookingID}/rooms`
        );
    }


    // ------------------------------------------------------
    // Create booking
    // ------------------------------------------------------

    async function submitBooking(
        payload: CreateHotelBookingFormPayload
    ) {
        if (
            !parsedUserId ||
            parsedUserId <= 0
        ) {
            notifications.show({
                color: "red",
                title:
                    t.missing,
                message:
                    "UserId missing in URL",
            });

            return false;
        }

        if (
            !parsedEventId ||
            parsedEventId <= 0
        ) {
            notifications.show({
                color: "red",
                title:
                    t.missing,
                message:
                    "EventId missing in URL",
            });

            return false;
        }

        setSaving(true);

        try {
            const response =
                await createHotelBooking(
                    apiUrl,
                    {
                        eventID:
                            parsedEventId,

                        hotelID:
                            payload.hotelID,

                        userID:
                            parsedUserId,

                        country:
                            payload.country,

                        contactName:
                            payload.contactName,

                        contactEmail:
                            payload.contactEmail,

                        whatsappCountryCode:
                            payload.whatsappCountryCode,

                        whatsappNumber:
                            payload.whatsappNumber,

                        comment:
                            payload.comment,

                        rooms:
                            payload.rooms,
                    }
                );

            notifications.show({
                color: "green",

                title:
                    t.bookingSent,

                message:
                    `Booking #${response.hotelBookingID} ${t.bookingSentMessage}`,
            });

            await loadUserBookings();

            return true;
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: t.error,
                message:
                    e?.message ||
                    t.cannotSubmit,
            });

            return false;
        } finally {
            setSaving(false);
        }
    }


    // ------------------------------------------------------
    // Render
    // ------------------------------------------------------

    return (
        <div>
            <HeroSectionV2
                titleEn={
                    texts.en.heroTitle
                }
                titleDa={
                    texts.da.heroTitle
                }
                descriptionEn={
                    texts.en.heroDesc
                }
                descriptionDa={
                    texts.da.heroDesc
                }
                imageSrc={
                    Billede1
                }
                badgeText="HOTELS"
            />

            <Container
                size="xl"
                py="md"
            >
                <Stack gap="md">
                    <Paper
                        withBorder
                        p="lg"
                        radius="md"
                    >
                        <Group
                            justify="space-between"
                            align="flex-start"
                        >
                            <Stack gap={4}>
                                <Badge
                                    variant="light"
                                    color="blue"
                                    w="fit-content"
                                >
                                    {t.event}:{" "}
                                    {selectedEvent?.eventName ||
                                        `Event ${parsedEventId}`}
                                </Badge>

                                <Title order={3}>
                                    {
                                        texts[
                                            language
                                        ]
                                            .heroTitle
                                    }
                                </Title>

                                <Text
                                    c="dimmed"
                                    size="sm"
                                >
                                    {
                                        texts[
                                            language
                                        ]
                                            .heroDesc
                                    }
                                </Text>
                            </Stack>
                        </Group>
                    </Paper>


                    <Tabs
                        value={
                            activeTab
                        }
                        onChange={
                            setActiveTab
                        }
                        variant="pills"
                        radius="md"
                        color="red"
                    >
                        <Tabs.List grow>
                            <Tabs.Tab
                                value="bookings"
                                leftSection={
                                    <IconBed
                                        size={
                                            16
                                        }
                                    />
                                }
                            >
                                {
                                    t.myBookings
                                }
                            </Tabs.Tab>

                            <Tabs.Tab
                                value="create"
                                leftSection={
                                    <IconPlus
                                        size={
                                            16
                                        }
                                    />
                                }
                            >
                                {
                                    t.createBooking
                                }
                            </Tabs.Tab>
                        </Tabs.List>


                        <Tabs.Panel
                            value="bookings"
                            pt="md"
                        >
                            <Paper
                                withBorder
                                p="lg"
                                radius="md"
                            >
                                <BookingList
                                    bookings={filteredBookings}
                                    loading={loadingBookings}
                                    search={bookingSearch}
                                    statusFilter={bookingStatusFilter}
                                    onSearchChange={setBookingSearch}
                                    onStatusFilterChange={setBookingStatusFilter}
                                    t={t}
                                    language={language}
                                    onView={openViewBooking}
                                    onPay={handlePayNow}
                                    onRooming={handleRoomingList}
                                />
                            </Paper>
                        </Tabs.Panel>


                        <Tabs.Panel
                            value="create"
                            pt="md"
                        >
                            <CreateBookingForm
                                hotels={
                                    hotels
                                }

                                saving={
                                    saving
                                }

                                t={
                                    t
                                }

                                language={
                                    language
                                }

                                onSubmit={
                                    submitBooking
                                }
                            />
                        </Tabs.Panel>
                    </Tabs>
                </Stack>
            </Container>


            <BookingDetailsModal
                opened={viewBookingOpen}
                booking={selectedBooking}
                onClose={() => setViewBookingOpen(false)}
                showEdit={false}
                t={t}
                language={language}
                onPay={handlePayNow}
                onRooming={handleRoomingList}
            />


            <EditBookingModal
                opened={editBookingOpen}
                booking={editingBooking}
                hotels={hotels}
                saving={savingEdit}
                language={language}
                t={t}
                onClose={() => {
                    if (!savingEdit) {
                        setEditBookingOpen(false);
                        setEditingBooking(null);
                    }
                }}
                onSave={saveBookingChanges}
            />
        </div>
    );
}